import { useScanner } from '../../../../hooks/scanner.hook'
import { useEffect, useState } from 'react'
import { getProductFromScan } from 'client/scanner/scanner.client'
import { manageError } from 'utils/errors/error.handler'
import { ProductWithStock, type AlertSuggestionProduct, type ProductInfo } from 'types/model'
import { useConfirmationDialog } from 'components/molecules/ConfirmationDialog.molecule'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { tabs } from './ScanProduct.helpers'
import { useAlertsRegisterRestockNavigator } from '../register-restock/RegisterRestock.navigator'
import { getActiveProductsFromSuggestion } from 'client/alerts/alerts.client'
import { useParams } from 'react-router-dom'
import { getProducts } from '../../../../client/inventory/inventory.client'
import { findProductsInfo } from '../../../../client/products/products.client'
import { useAtom } from 'jotai/index'
import { AlertsSelectedAtom, ProductSelectedAtom } from '../Alerts.state'
import { useAlertsPageNavigator } from 'pages/alerts/Alerts.navigator'
import { useFeatureIsOn } from '@growthbook/growthbook-react'

export const useScanProduct = () => {
  const { t } = useTranslation('global')
  const alertsPageNavigator = useAlertsPageNavigator()

  const [, setAlertSelected] = useAtom(AlertsSelectedAtom)
  const [, setProductSelected] = useAtom(ProductSelectedAtom)

  const [loading, setLoading] = useState(false)
  const [alerts, setAlerts] = useState<AlertSuggestionProduct[]>([])
  const [products, setProducts] = useState<ProductWithStock[]>([])
  const [activeTab, setActiveTab] = useState('Urgent')

  const isProductDemandOn = useFeatureIsOn("op_product_demand");
  const isProductCategoryOn = useFeatureIsOn("op_product_category");

  const {
    keys,
    getScan
  } = useScanner()
  const {
    dialogRef: typeProductDialogRef,
    open: openTypeProductDialog
  } = useConfirmationDialog()
  const { source } = useParams()

  const registerNavigator = useAlertsRegisterRestockNavigator()

  const onChangeTab = (selectedTab: string) => {
    setActiveTab(selectedTab)
  }

  const getTabs = () => {
    return tabs.map((id) => ({
      id,
      label: t(`alerts.scan-product.tabs.${id}`, {
        counter: alerts.filter(p => p.level === id).length || 0
      })
    }))
  }

  const searchProduct = (ean: string) => {
    setLoading(true)
    getProductFromScan(ean)
      .then(e => {
        const productSelected = products.find(p => p.ean === e.product.ean)
        const alertSelected = alerts.find((a) => a.productEan === e.product.ean)
        if (productSelected) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          registerNavigator.go(source!, ean)
          setAlertSelected(alertSelected)
          setProductSelected(productSelected)
        } else {
          toast.error(t('alerts.scan-product.error-not-in-list', { product: e.product.name }))
        }
      })
      .catch(e => {
        console.error(e)
        toast.error('Error al obtener producto: ' + ean)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  /* On init */
  useEffect(() => {
    if (source) {
      setLoading(true)
      Promise.all([
        getActiveProductsFromSuggestion({suggestion:source, isProductDemandOn, isProductCategoryOn}),
        getProducts(source)
      ]).then(async ([alerts, products]) => {
        if(products.length > 0) {
          setAlerts(alerts)
          const res = await findProductsInfo(products.map((p) => p.productEan))
          setProducts(res.map((it): ProductWithStock => {
            return { ...it, stock: products.find((p) => p.productEan === it.ean)?.stock ?? 0 }
          }))
        }
        else{
          alertsPageNavigator.go()
          toast.error(t('alerts.scan-product.error-no-products'))
        }
      }).catch((err: Error) => {
        manageError(err.message)
      }).finally(() => {
        setLoading(false)
      })
    }
  }, [])

  /* On scan */
  useEffect(() => {
    const scanned = getScan()
    if (scanned) {
      searchProduct(scanned)
    }
  }, [keys])

  return {
    source,
    hasAlerts: alerts.length > 0,
    totalAlerts: alerts.length,
    alerts: alerts.filter((a) => a.level === activeTab),
    products,
    isLoading: loading,
    typeProductDialogRef,
    actions: {
      openTypeProductDialog,
      searchProduct,
      onChangeTab,
      getTabs,
      close: () => {
        alertsPageNavigator.go()
      }
    }
  }
}
